import { ReceiptData } from '../types';

const GENKIT_URL = import.meta.env.VITE_GENKIT_URL || 'http://localhost:3400';

const RATES_CACHE_KEY = 'paycircle_rates_cache';
const RATES_CACHE_TIME_KEY = 'paycircle_rates_timestamp';
const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

// --- OFFLINE REGION DETECTION DATA ---
// Simple bounding box data for major regions to support offline detection
const COUNTRY_BOUNDS = [
  { country: 'USA', currency: 'USD', lang: 'en', lat: [24, 49], lng: [-125, -66] },
  { country: 'UK', currency: 'GBP', lang: 'en', lat: [49, 61], lng: [-8, 2] },
  { country: 'India', currency: 'INR', lang: 'en', lat: [8, 37], lng: [68, 97] },
  { country: 'Eurozone', currency: 'EUR', lang: 'en', lat: [35, 70], lng: [-10, 30] }, // Simplified Europe
  { country: 'China', currency: 'CNY', lang: 'zh', lat: [18, 54], lng: [73, 135] },
  { country: 'Brazil', currency: 'BRL', lang: 'pt', lat: [-34, 5], lng: [-74, -34] },
  { country: 'Russia', currency: 'RUB', lang: 'ru', lat: [41, 82], lng: [19, 190] },
  { country: 'Japan', currency: 'JPY', lang: 'ja', lat: [30, 45], lng: [128, 146] },
  { country: 'Indonesia', currency: 'IDR', lang: 'id', lat: [-11, 6], lng: [95, 141] },
  { country: 'Australia', currency: 'AUD', lang: 'en', lat: [-44, -10], lng: [112, 154] },
  { country: 'Canada', currency: 'CAD', lang: 'en', lat: [42, 83], lng: [-141, -52] },
  { country: 'Mexico', currency: 'MXN', lang: 'es', lat: [14, 33], lng: [-118, -86] },
  { country: 'Middle East', currency: 'SAR', lang: 'ar', lat: [12, 35], lng: [34, 60] }, // Simplified
];

const getOfflineRegionSettings = (lat: number, lng: number) => {
  const match = COUNTRY_BOUNDS.find(b =>
    lat >= b.lat[0] && lat <= b.lat[1] &&
    lng >= b.lng[0] && lng <= b.lng[1]
  );
  if (match) {
    return { currency: match.currency, language: match.lang, languageName: 'Detected Offline' };
  }
  return null;
};

export const parseReceipt = async (_base64Image: string): Promise<ReceiptData & { category?: string }> => {
  return {
    total: 0,
    currency: 'USD',
    date: new Date().toISOString(),
    vendor: '',
    items: []
  };
};

export const fetchRealTimeRates = async (): Promise<Record<string, number>> => {
  const cachedRates = localStorage.getItem(RATES_CACHE_KEY);
  const cachedTime = localStorage.getItem(RATES_CACHE_TIME_KEY);
  if (cachedRates && cachedTime && Date.now() - parseInt(cachedTime) < CACHE_DURATION) {
    return JSON.parse(cachedRates);
  }

  try {
    const response = await fetch(`${GENKIT_URL}/fetchRatesFlow`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: null })
    });
    if (!response.ok) return {};
    const json = await response.json();
    const rates = json.result || {};
    if (Object.keys(rates).length > 0) {
      rates['USD'] = 1;
      localStorage.setItem(RATES_CACHE_KEY, JSON.stringify(rates));
      localStorage.setItem(RATES_CACHE_TIME_KEY, Date.now().toString());
    }
    return rates;
  } catch (error) {
    console.error("Genkit Rate Fetch Error:", error);
    return {};
  }
};

export const suggestRegionSettings = async (lat: number, lng: number): Promise<{ currency: string; language: string; languageName: string } | null> => {
  if (!navigator.onLine) {
    return getOfflineRegionSettings(lat, lng);
  }

  try {
    const response = await fetch(`${GENKIT_URL}/suggestRegionFlow`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: { lat, lng } })
    });
    if (!response.ok) return getOfflineRegionSettings(lat, lng);
    const json = await response.json();
    const result = json.result;
    if (result && result.currency === 'INR') {
      result.language = 'en';
      result.languageName = 'English';
    }
    return result;
  } catch (error) {
    console.error("Error suggesting region settings via Genkit:", error);
    return getOfflineRegionSettings(lat, lng);
  }
};